import React, { useEffect, useState } from "react"
import { ItemPicker, SelectedSortingOption } from "@tenforce/product-fields"
import { Filters } from "@tenforce/product-fields/itemPicker/types"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { SortingOption } from "@tenforce/product-fields/viewSettings"

import { IItemPickerField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../../util/Translations"
import { addErrorNotification } from "../../../../notifications/actions"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import {
    fetchAvailableSortingFields,
    fetchCollectionItems as fetchCollectionItemsThunk,
    fetchConceptFields,
    getCollectionQuery,
    updateCollectionQuery,
} from "../../../../collection/slice"
import { FetchCollectionParams } from "../../../../collection"
import { Field } from "../../Field"
import { IItem } from "../../../../../models"
import { formatString } from "../../../../../util/Strings"
import { PAGE_SIZE } from "../../../../thumbtableview/slice"
import { updateField } from "../../../slice"
import { getItemAncestry } from "../../../../../apis/items"

type Props = IItemPickerField

// TODO: These are just to trick tailwind into generating the dynamically created classes from the product-fields.
// It should at some point be removed once the SCSS is generated properly.
const _levelOne = "tw-ml-0"
const _levelTwo = "tw-ml-4"
const _levelThree = "tw-ml-8"
const _levelFour = "tw-ml-12"
const _levelFive = "tw-ml-16"

type SortingLevel = 0 | 1 | 2 | null

const ItemPickerNew: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [itemsWithParents, setItemsWithParents] = useState<IItem[]>([])
    const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([])
    const [isSortingLoading, setIsSortingLoading] = useState<boolean>(false)
    const [isFieldSelectionLoading, setIsFieldSelectionLoading] =
        useState<boolean>(true)

    const {
        description,
        isSingleSelect,
        value: selectedItems,
        isDisabled,
        itemId,
        fieldId,
        title,
        odataType,
        isLoading = false,
        itemType,
        ...rest
    } = props

    const { isLoading: globalIsLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const {
        visibleFields,
        isQueryProcessing,
        visibleFieldsIds,
        selectedSorting = [],
        availableSortingOptions,
        selectableFieldsData,
        items,
        remainingCount = 0,
        filters,
    } = useAppSelector((state: GlobalState) => state.collections[fieldId] || {})
    const [localSelectedSorting, setLocalSelectedSorting] =
        useState<SortingOption[]>(selectedSorting)
    const [activeSorting, setActiveSorting] = useState<SortingLevel>(null)
    const fetchAncestry = async (itemId: number): Promise<IItem[]> => {
        try {
            let parents = await getItemAncestry(itemId)
            if (parents.length > 0) {
                parents = parents.map((parent) => ({
                    ...parent,
                    url: getItemDetailUrl(env.applicationPath, "", parent.id),
                }))
            }
            return parents
        } catch (error) {
            dispatch(
                addErrorNotification(
                    error instanceof Error
                        ? error.message
                        : "Something went wrong when fetching ancestry.",
                ),
            )
            return []
        }
    }

    useEffect(() => {
        const fetchParentsForItems = async (): Promise<void> => {
            const updatedItems = []
            const selectedIds: number[] = []
            for (const item of selectedItems) {
                selectedIds.push(item.id)
                const parentsData = await fetchAncestry(item.id)
                updatedItems.push({
                    ...item,
                    url: getItemDetailUrl(env.applicationPath, "", item.id),
                    parents: parentsData.filter(
                        (item) => item.id === 0 || item.id,
                    ),
                })
            }
            setSelectedItemsIds(selectedIds)
            setItemsWithParents(updatedItems)
        }
        fetchParentsForItems()
    }, [selectedItems])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = async (): Promise<void> => {
        await dispatch(getCollectionQuery({ itemId, fieldId, odataType }))
        await fetchCollectionItems()
    }

    const fetchCollectionItems = async (): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: 0,
                top: PAGE_SIZE,
            }),
        )
    }

    const fetchMoreCollectionItems = async (): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: items.length,
                top: PAGE_SIZE,
            }),
        )
    }

    const skipValueIds = (activeLevel: SortingLevel): string[] => {
        if (activeLevel === 1) {
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                return localSelectedSorting[0].columnId
                    ? [localSelectedSorting[0].columnId]
                    : []
            }
            return []
        }

        if (activeLevel === 2) {
            const ids: string[] = []
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                if (localSelectedSorting[0].columnId) {
                    ids.push(localSelectedSorting[0].columnId)
                }
                if (localSelectedSorting[1]?.columnId) {
                    ids.push(localSelectedSorting[1].columnId)
                }
            }
            return ids
        }

        return []
    }

    const fetchSortingFields = async (
        activeLevel: SortingLevel,
    ): Promise<void> => {
        setIsSortingLoading(true)
        setActiveSorting(activeLevel)
        const localSelectedId = skipValueIds(activeLevel)

        await dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                skip: 0,
                top: 20,
                localSelectedSortingId: localSelectedId,
            }),
        )
        setIsSortingLoading(false)
    }

    const fetchSelectableFields = async (): Promise<void> => {
        setIsFieldSelectionLoading(true)
        await dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                top: 20,
                skip: 0,
            }),
        )
        setIsFieldSelectionLoading(false)
    }

    const onSaveViewSettings = async (settingsData: {
        selectedFields?: string[]
        sorting?: SelectedSortingOption[]
    }): Promise<void> => {
        const apiPayload: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            selectedFields: settingsData.selectedFields
                ? settingsData.selectedFields
                : visibleFieldsIds,
            sorting:
                settingsData?.sorting?.map(
                    (option: SelectedSortingOption): SortingOption => {
                        return {
                            mode: option.mode,
                            columnId: option.columnId,
                            label: option.label,
                        }
                    },
                ) ?? [],
        }

        await dispatch(updateCollectionQuery(apiPayload)).unwrap()
        if (settingsData.sorting) {
            setLocalSelectedSorting(settingsData.sorting)
        }
        setActiveSorting(0)
        fetchCollectionData()
    }

    const onFilterChange = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: 20,
                skip: 0,
            }),
        )
    }

    const loadMoreSelectableValue = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: 20,
                skip: selectableFieldsData.currentSkip,
            }),
        )
    }

    const _onSave = async (itemIds: number[]): Promise<void> => {
        await dispatch(
            updateField({
                field: props,
                newValue: itemIds,
            }),
        ).unwrap()
    }

    const onSortingFilterChange = (filter: string): void => {
        setIsSortingLoading(true)
        dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: 0,
                top: 20,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        )
        setIsSortingLoading(false)
    }

    const onSortingLoadMore = (filter: string): void => {
        setIsSortingLoading(true)
        const nextSkip = availableSortingOptions?.currentSkip || 0
        dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: nextSkip,
                top: 20,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        )
        setIsSortingLoading(false)
    }

    const onCollectionFiltersChange = async (
        filters: Filters,
    ): Promise<void> => {
        const args: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            filters,
        }

        await dispatch(updateCollectionQuery(args)).unwrap()
        fetchCollectionData()
    }

    const {
        ClickHereToEdit,
        Columns,
        FilterAllNValues,
        FilterNSelectedValues,
        FilterOnX,
        ItemOverviewSorting,
        ItemPickerAddItem,
        ItemPickerAddItems,
        ItemPickerEditItem,
        ItemPickerEditItems,
        ItemboxFilter,
        LoadNMore,
        LoadNMoreOf,
        NoItems,
        NothingFound,
        OpenViewSettings,
        ShowAllValues,
        ShowSelectedValues,
        SortLevelOne,
        SortLevelThree,
        SortLevelTwo,
        ViewSettings,
    } = Labels

    const regionalDecimalSeparator = useAppSelector(
        (state: GlobalState) =>
            state.userFormattingSettings.numberFormat.decimalSeparator,
    )

    return (
        <ItemPicker
            {...rest}
            regionalDecimalSeparator={regionalDecimalSeparator}
            isDisabled={isFieldDisabled}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            fieldId={fieldId}
            title={title}
            isLoading={globalIsLoading || isLoading}
            isSingleSelect={isSingleSelect}
            labels={{
                AddItem: ItemPickerAddItem,
                AddItems: ItemPickerAddItems,
                EditItem: ItemPickerEditItem,
                EditItems: ItemPickerEditItems,
                EmptyPlaceholder: ClickHereToEdit,
                FieldSelectionSectionLabel: Columns,
                FilterAllNValues: (count) =>
                    Symbols.Search + formatString(FilterAllNValues, count),
                FilterOnX: (fieldName) =>
                    formatString(FilterOnX, fieldName.toLowerCase()),
                FilterNSelectedValues: (count) =>
                    Symbols.Search + formatString(FilterNSelectedValues, count),
                FilterNoResults: NothingFound,
                LoadNMore: (remaining) => formatString(LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(LoadNMoreOf, pageSize, remaining),
                NoItems: NoItems,
                OpenViewSettings: OpenViewSettings,
                ShowAllValuesTooltip: ShowAllValues,
                ShowSelectedValuesTooltip: ShowSelectedValues,
                SortLevelOne: SortLevelOne,
                SortLevelThree: SortLevelThree,
                SortLevelTwo: SortLevelTwo,
                SortingSectionLabel: ItemOverviewSorting,
                ToggleFilter: ItemboxFilter,
                ViewSettings: ViewSettings,
            }}
            value={itemsWithParents}
            collection={{
                filters: filters,
                visibleFields: visibleFields,
                selectedItemIds: selectedItemsIds,
                visibleFieldsIds: visibleFieldsIds,
                isQueryProcessing: isQueryProcessing || isLoading,
                items: items,
                remainingCount: remainingCount,
            }}
            fieldSelection={{
                isLoading: isFieldSelectionLoading,
                onFilterChange: onFilterChange,
                onLoadMore: loadMoreSelectableValue,
                selectableFields: {
                    data: selectableFieldsData?.allConceptFields || [],
                    totalCount: selectableFieldsData?.totalValueCount || 0,
                    currentSkip: selectableFieldsData?.currentSkip || 0,
                    hasMore: selectableFieldsData?.hasMore || false,
                },
                fetchOptions: fetchSelectableFields,
            }}
            sorting={{
                isLoading: isSortingLoading,
                fetchSelectable: fetchSortingFields,
                selected: selectedSorting.map((sorting) => {
                    return {
                        columnId: sorting.columnId,
                        mode: sorting.mode,
                        label: sorting.label,
                    }
                }),
                localSelected: localSelectedSorting,
                selectableOptions: {
                    data: availableSortingOptions?.selectableOptions || [],
                    hasMore: availableSortingOptions?.hasMore || false,
                    totalCount: availableSortingOptions?.totalValueCount || 0,
                    currentSkip: availableSortingOptions?.currentSkip || 0,
                },
                onFilterChange: onSortingFilterChange,
                onLoadMore: onSortingLoadMore,
                onChange: (sorting: SortingOption[]): void =>
                    setLocalSelectedSorting(sorting),
            }}
            fetchCollectionData={fetchCollectionData}
            onSaveFilters={onCollectionFiltersChange}
            onSaveViewSettings={onSaveViewSettings}
            getItemField={(itemField): JSX.Element => {
                return (
                    <Field
                        field={{ ...itemField, itemType }}
                        isLoading={false}
                    />
                )
            }}
            onSave={_onSave}
            onLoadMore={fetchMoreCollectionItems}
        />
    )
}

export default ItemPickerNew
